import { useEffect, useReducer, useState } from 'react'

import { useCarDetailsCardConfig } from '../../stages/car-details'

export type State = {
  translation: null | number
  internalActiveGroup: number
  disableTransition: boolean
}

export type Props = {
  slidesCount: number
  slideWidth: number | null
  slidesPerGroup: number
  onActiveGroupChange?(group: number): void
  slidesPerPage: number
  activeGroup: { value: number }
}

export const useSliderTranslation = ({
  slidesCount,
  slideWidth,
  slidesPerGroup,
  onActiveGroupChange,
  slidesPerPage,
  activeGroup
}: Props) => {
  const { gallery } = useCarDetailsCardConfig()
  /* maxTranslation высчитывает максимально возможный translation, который не будет создавать 
     пустого места справа слайдера. Используется обычно, если функция
     willTranslationToThisGroupCreateEmptySpace вернет true, то мы задаем группе maxTranslation
   */
  const translationToLastGroup = -(slideWidth === null ? 0 : slideWidth) * (slidesCount - slidesPerPage)
  /* Проверка больше ли нуля нужна, т.к. положительный translation означает, что первая группа не заполнена, 
     и если задать translationToLastGroup, то слайдер прижмется к правому краю
   */
  const maxTranslation = translationToLastGroup > 0 ? 0 : translationToLastGroup
  const groupsCount = Math.ceil(slidesCount / slidesPerGroup)

  /* Округление вврех нужно по следующей причине:
     Если мы передадим в качестве параметра maxTranslation, то может получиться дробная группа,
     (например на slidesPerPage = 3, slidesPerGroup = 3, slidesCount = 4, maxTranslation будет равен ширине 
     первого слайда, что даст группу 0.33). Поэтому если мы получаем дробное число, то это значит,
     что мы пытаемся
   */
  const getshowLocationAsTranslation = (translation: number) =>
    -(translation / (slidesPerGroup * (slideWidth === null ? 0 : slideWidth)))

  //Функция которая считает смещение на которое нужно подвинуть слайд
  const getTranslationByGroup = (group: number) => {
    if (slideWidth !== null) {
      return -(slideWidth * group * slidesPerGroup)
    } else {
      return null
    }
  }

  /* Если группа не до конца заполнена слайдами (неполная группа может быть только последней),
     то обычный getTranslationByGroup(group) будет давать такой trasnalation, что в слайдере 
     справа появится пустое пространство из-за отсутствующих с группе слайдов. Эта функция нужна, 
     чтобы проверять такие случаии. 
   */
  const willTranslationToThisGroupCreateEmptySpace = (group: number) => {
    const translationByGroup = getTranslationByGroup(group)
    if (translationByGroup !== null) {
      return translationByGroup < maxTranslation
    } else {
      return false
    }
  }

  const reducer = (state: State, payload: { group: number; disableTransition?: boolean }) => {
    return {
      ...state,
      internalActiveGroup: payload.group,
      translation: willTranslationToThisGroupCreateEmptySpace(payload.group)
        ? maxTranslation
        : getTranslationByGroup(payload.group),
      disableTransition: !!payload.disableTransition
    }
  }

  const [state, setInternalActiveGroup] = useReducer(reducer, {
    translation: null,
    internalActiveGroup: activeGroup.value,
    disableTransition: false
  })

  const isShiftPrevPossible = (value: number) => state.internalActiveGroup + value > -1
  const isShiftNextPossible = (value: number) => state.internalActiveGroup + value < groupsCount

  const [isDisabledButton, setIsDisabledButton] = useState(false)
  const shiftSlider = (value: number) => {
    if (slideWidth) {
      setIsDisabledButton(true)
      if (!isShiftNextPossible(value)) {
        if (gallery?.circularScroll) setInternalActiveGroup({ group: 0 })
        else setInternalActiveGroup({ group: groupsCount - 1 })
      } else if (!isShiftPrevPossible(value)) {
        if (gallery?.circularScroll) setInternalActiveGroup({ group: groupsCount - 1 })
        else setInternalActiveGroup({ group: 0 })
      } else if (value === 0) {
        setInternalActiveGroup({ group: state.internalActiveGroup })
      } else {
        /* Т.к. на последней странице может быть много групп, а визуально их смена никак не меняется
           (обычно активная группа прижимается к левому краю, но тут такого нельзя допускать, чтобы
            не появлялось пустое пространство). Поэтому при свайпе влево мы сразу считаем активную группу от
            крайней левой, чтобы визуально смена группы была сразу.
         */
        if (value < 0 && willTranslationToThisGroupCreateEmptySpace(state.internalActiveGroup)) {
          /* Округление вверх нужно, т.к. maxTranslation может не делиться на ширину группы
           */
          setInternalActiveGroup({
            group: Math.max(0, Math.ceil(getshowLocationAsTranslation(maxTranslation)) + value)
          })
        } else {
          setInternalActiveGroup({ group: state.internalActiveGroup + value })
        }
      }
    }
  }

  const setActiveSlideForClone = () => {
    let internalActiveGroupCurrent
    if (state.internalActiveGroup === 0) {
      internalActiveGroupCurrent = groupsCount - 2
      setInternalActiveGroup({ group: internalActiveGroupCurrent, disableTransition: true })
    } else if (state.internalActiveGroup + 1 === groupsCount) {
      internalActiveGroupCurrent = 1
      setInternalActiveGroup({ group: internalActiveGroupCurrent, disableTransition: true })
    }
  }

  useEffect(() => {
    setInternalActiveGroup({ group: activeGroup.value })
  }, [activeGroup, slidesPerPage])

  useEffect(() => {
    setInternalActiveGroup({ group: state.internalActiveGroup, disableTransition: true })
  }, [slideWidth, state.internalActiveGroup])

  useEffect(() => {
    onActiveGroupChange && onActiveGroupChange(state.internalActiveGroup)
  }, [state.internalActiveGroup, onActiveGroupChange, slidesPerPage])

  return {
    shiftSlider,
    state,
    isShiftPrevPossible,
    isShiftNextPossible,
    maxTranslation,
    setActiveSlideForClone,
    isDisabledButton,
    setIsDisabledButton
  }
}
